import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  GridEventListener,
  gridClasses,
  GridRowClassNameParams,
} from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useDialogs } from '../../hooks/useDialogs/useDialogs';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import PageContainer from '../layout/PageContainer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import type { InventoryItem } from '../../models/InventoryItem';
import { GET_INVENTORYITEMS_QUERY } from '../../queries/InventoryItemQueries';
import { DELETE_INVENTORYITEM_BY_ID_MUTATION } from '../../mutations/InventoryItemMutations';

const INITIAL_PAGE_SIZE = 100;

export default function InventoryItemList() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const dialogs = useDialogs();
  const notifications = useNotifications();

  const [getInventoryItems, { loading: getInventoryItemsLoading, error: getInventoryItemsError, data }] = useLazyQuery(
    GET_INVENTORYITEMS_QUERY);

  const [deleteInventoryItemById] = useMutation(
    DELETE_INVENTORYITEM_BY_ID_MUTATION, {
    refetchQueries: [{ query: GET_INVENTORYITEMS_QUERY }],
  });

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: searchParams.get('page') ? Number(searchParams.get('page')) : 0,
    pageSize: searchParams.get('pageSize')
      ? Number(searchParams.get('pageSize'))
      : INITIAL_PAGE_SIZE,
  });

  const [filterModel, setFilterModel] = useState<GridFilterModel>(
    searchParams.get('filter')
      ? JSON.parse(searchParams.get('filter') ?? '')
      : { items: [] },
  );

  const [sortModel, setSortModel] = useState<GridSortModel>(
    searchParams.get('sort') ? JSON.parse(searchParams.get('sort') ?? '') : [],
  );

  const [rowsState, setRowsState] = useState<{
    rows: InventoryItem[];
    rowCount: number;
  }>({
    rows: [],
    rowCount: 0,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(model);

      searchParams.set('page', String(model.page));
      searchParams.set('pageSize', String(model.pageSize));

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      setFilterModel(model);

      if (
        model.items.length > 0 ||
        (model.quickFilterValues && model.quickFilterValues.length > 0)
      ) {
        searchParams.set('filter', JSON.stringify(model));
      } else {
        searchParams.delete('filter');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model);

      if (model.length > 0) {
        searchParams.set('sort', JSON.stringify(model));
      } else {
        searchParams.delete('sort');
      }

      const newSearchParamsString = searchParams.toString();

      navigate(
        `${pathname}${newSearchParamsString ? '?' : ''}${newSearchParamsString}`,
      );
    },
    [navigate, pathname, searchParams],
  );

  const loadData = useCallback(async () => {
    setError(null);
    setIsLoading(true);

    try {
      getInventoryItems();

      if (getInventoryItemsLoading) {
        setIsLoading(true);
      }

      if (getInventoryItemsError) {
        setError(getInventoryItemsError);
      }

      const inventoryItems = data?.inventoryItems || [];

      setRowsState({
        rows: inventoryItems,
        rowCount: inventoryItems.length || 0,
      });
    } catch (listDataError) {
      setError(listDataError as Error);
    }

    setIsLoading(false);
  }, [getInventoryItems, getInventoryItemsLoading, getInventoryItemsError, data?.inventoryItems]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = useCallback(() => {
    if (!isLoading) {
      loadData();
    }
  }, [isLoading, loadData]);

  const handleRowClick = useCallback<GridEventListener<'rowClick'>>(
    ({ row }) => {
      navigate(`/dashboard/inventory-items/${row.id}`);
    },
    [navigate],
  );

  const handleRowEdit = useCallback(
    (inventoryItem: InventoryItem) => () => {
      navigate(`/dashboard/inventory-items/${inventoryItem.id}/edit`);
    },
    [navigate],
  );

  const handleRowDelete = useCallback(
    (inventoryItem: InventoryItem) => async () => {
      const confirmed = await dialogs.confirm(
        `Do you wish to delete ${inventoryItem.item?.name}?`,
        {
          title: `Delete inventory item?`,
          severity: 'error',
          okText: 'Delete',
          cancelText: 'Cancel',
        },
      );

      if (confirmed) {
        setIsLoading(true);
        try {
          const { data: result } = await deleteInventoryItemById({ variables: { id: inventoryItem.id } });

          if (result?.deleted) {
            notifications.show('Inventory item deleted successfully.', {
              severity: 'success',
              autoHideDuration: 3000,
            });

            await loadData();
          } else {
            notifications.show(
              'Failed to delete Inventory item. Reason: Unknown.',
              {
                severity: 'error',
                autoHideDuration: 3000,
              },
            );
          }
        } catch (deleteError) {
          notifications.show(
            `Failed to delete Inventory item. Reason:' ${(deleteError as Error).message}`,
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }
        setIsLoading(false);
      }
    },
    [dialogs, deleteInventoryItemById, notifications, loadData],
  );

  const initialState = useMemo(
    () => ({
      pagination: { paginationModel: { pageSize: INITIAL_PAGE_SIZE } },
    }),
    [],
  );

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'itemName',
        headerName: 'Name',
        width: 300,
        valueGetter: (_, row) => row.item?.name
      },
      {
        field: 'quantityOnHand',
        headerName: 'QOH (lbs)',
        type: 'number',
        width: 100,
        valueFormatter: (value: number | undefined) => (value || 0).toFixed(3),
        rowSpanValueGetter: () => null
      },
      {
        field: 'lotNumber',
        headerName: 'Lot Number',
        width: 200,
        rowSpanValueGetter: () => null
      },
      {
        field: 'vendor',
        headerName: 'Vendor',
        valueGetter: (_, row) => row.vendor?.name,
        rowSpanValueGetter: () => null,
        width: 200
      },
      {
        field: 'orderDetails',
        headerName: 'Order Details',
        width: 250,
        rowSpanValueGetter: () => null
      },
      {
        field: 'outOfStock',
        headerName: 'Out of Stock?',
        width: 125,
        valueGetter: (_, row) => row.outOfStock ? 'Yes' : 'No',
        rowSpanValueGetter: () => null
      },
      {
        field: 'actions',
        type: 'actions',
        flex: 1,
        align: 'right',
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="edit-item"
            icon={<EditIcon sx={{ fontSize: 16 }} />}
            label="Edit"
            onClick={handleRowEdit(row)}
          />,
          <GridActionsCellItem
            key="delete-item"
            icon={<DeleteIcon sx={{ fontSize: 16 }} />}
            label="Delete"
            onClick={handleRowDelete(row)}
          />,
        ],
      },
    ],
    [handleRowEdit, handleRowDelete],
  );

  const getRowClassName = (params: GridRowClassNameParams) => {
    if (params.row.item?.outOfStock) {
      return 'out-of-stock';
    }

    if (params.row.item?.lowStock) {
      return 'low-stock';
    }

    return '';
  }

  const pageTitle = 'Inventory Items';

  return (
    <PageContainer
      title={pageTitle}
      breadcrumbs={[{ title: pageTitle }]}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Reload data" placement="right" enterDelay={1000}>
            <div>
              <IconButton size="small" aria-label="refresh" onClick={handleRefresh}>
                <RefreshIcon />
              </IconButton>
            </div>
          </Tooltip>
        </Stack>
      }
    >
      <Box sx={{ flex: 1, width: '100%' }}>
        {error ? (
          <Box sx={{ flexGrow: 1 }}>
            <Alert severity="error">{error.message}</Alert>
          </Box>
        ) : (
          <Box sx={{ height: '64vh', width: '100%' }}>
            <DataGrid
              density="compact"
              rows={rowsState.rows}
              columns={columns}
              pagination
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              getRowClassName={getRowClassName}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
              disableRowSelectionOnClick
              onRowClick={handleRowClick}
              loading={isLoading || getInventoryItemsLoading}
              initialState={initialState}
              showToolbar
              rowSpanning
              showCellVerticalBorder
              showColumnVerticalBorder
              pageSizeOptions={[5, INITIAL_PAGE_SIZE, 25]}
              sx={{
                [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                  outline: 'transparent',
                },
                [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
                {
                  outline: 'none',
                },
                [`& .${gridClasses.row}:hover`]: {
                  cursor: 'pointer',
                },
                '.out-of-stock': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ?
                    'rgba(198, 40, 40, 1)' :
                    'rgba(239, 83, 80, 1)',
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ?
                      'rgba(198, 40, 40, 1)' :
                      'rgba(239, 83, 80, 1)',
                    filter: 'brightness(1.1)'
                  }
                },
                '.low-stock': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' ?
                    'rgba(230, 81, 0, 1)' :
                    'rgba(255, 152, 0, 1)',
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ?
                      'rgba(230, 81, 0, 1)' :
                      'rgba(255, 152, 0, 1)',
                    filter: 'brightness(1.1)'
                  }
                }
              }}
              slotProps={{
                loadingOverlay: {
                  variant: 'circular-progress',
                  noRowsVariant: 'circular-progress',
                },
                baseIconButton: {
                  size: 'small',
                },
              }}
            />
          </Box>
        )}
      </Box>
    </PageContainer>
  );
}